/**
 * Front-end rotation. Every constant below is the React reference value:
 *   INITIAL_DELAY_MS, ROTATION_INTERVAL_MS → src/features/Home/Hero/hero.constants.ts
 *   TYPE_SPEED_MS, DELETE_SPEED_MS         → src/features/Home/Hero/useWordTypewriter.ts
 */
const INITIAL_DELAY_MS = 1200;
const ROTATION_INTERVAL_MS = 3750;
const TYPE_SPEED_MS = 60;
const DELETE_SPEED_MS = 30;
const CURSOR_CLASSES = [
	'rotating-words-cursor',
	'border-r-2',
	'border-black/45',
	'pr-0.5',
];

// Deletes `from` char by char, then types `to` — the same two-phase transition
// HeroRotatingAccent does through useWordTypewriter. The first step of each
// phase runs immediately, as it does in the React effect body.
function animateTo( el, sizer, from, to, onDone ) {
	let current = from;
	let i = 0;

	// The sizer reserves the box width (see initTypewriter): the longer of the two
	// words while deleting, the target word while typing.
	sizer.textContent = from.length > to.length ? from : to;

	function typeStep() {
		if ( i === 0 ) {
			sizer.textContent = to;
		}
		if ( i >= to.length ) {
			return;
		}
		i += 1;
		el.textContent = to.slice( 0, i );
		if ( i >= to.length ) {
			onDone();
			return;
		}
		setTimeout( typeStep, TYPE_SPEED_MS );
	}

	function deleteStep() {
		if ( current.length > 0 ) {
			current = current.slice( 0, -1 );
			el.textContent = current;
			setTimeout( deleteStep, DELETE_SPEED_MS );
			return;
		}
		typeStep();
	}

	deleteStep();
}

// Replay chroma-sweep every time a word finishes typing.
function playChromaSweep( el ) {
	el.classList.remove( 'chroma-sweep-animate' );
	void el.offsetWidth;
	el.classList.add( 'chroma-sweep-animate' );
}

function initTypewriter( block ) {
	let words = [];
	try {
		words = JSON.parse( block.dataset.words || '[]' );
	} catch {
		words = [];
	}

	if ( ! Array.isArray( words ) || words.length === 0 ) {
		return;
	}

	const wordEl = block.querySelector( '.rotating-words-word' );
	if ( ! wordEl ) {
		return;
	}

	// Reduced motion: the static first word from save.js stays, no typing at all
	// (HeroRotatingAccent renders its pre-hydration span in that case).
	if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return;
	}

	// Runtime structure, mirroring HeroRotatingAccentAnimated (built here, not in
	// save.js, so crawlers/no-JS visitors keep the plain static word and the
	// stored markup needs no deprecation):
	//
	//   block (inline-grid)
	//     sizer  — invisible copy of the word being reserved, same cell
	//     live   — same cell: the word + the cursor
	//
	// The box width is the reserved word, so the <h1> wraps once per word and
	// never per letter (it flipped between two and three lines at 390 px before).
	// The cursor is its own element, never the word: `.chroma-sweep-animate` and
	// `.rotating-words-cursor` both set the `animation` shorthand, and on one
	// element the sweep would cancel the blink.
	block.classList.add( 'rotating-words-box' );
	const sizer = document.createElement( 'span' );
	sizer.className = 'rotating-words-sizer';
	sizer.setAttribute( 'aria-hidden', 'true' );
	const live = document.createElement( 'span' );
	live.className = 'rotating-words-live';
	const cursor = document.createElement( 'span' );
	cursor.className = CURSOR_CLASSES.join( ' ' );
	cursor.setAttribute( 'aria-hidden', 'true' );
	block.insertBefore( sizer, wordEl );
	live.appendChild( wordEl );
	live.appendChild( cursor );
	block.appendChild( live );

	// React re-types the first word on hydration (useWordTypewriter starts from
	// an empty string), so the WP surface does the same.
	animateTo( wordEl, sizer, '', words[ 0 ], () => playChromaSweep( wordEl ) );

	if ( words.length < 2 ) {
		return;
	}

	let wordIndex = 0;

	setTimeout( () => {
		setInterval( () => {
			const previous = words[ wordIndex ];
			wordIndex = ( wordIndex + 1 ) % words.length;
			animateTo( wordEl, sizer, previous, words[ wordIndex ], () =>
				playChromaSweep( wordEl )
			);
		}, ROTATION_INTERVAL_MS );
	}, INITIAL_DELAY_MS );
}

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( '.wp-block-avoid-content-rotating-words' )
		.forEach( initTypewriter );
} );
